// DesignAPIs.js
import { getToken } from "../utils/auth";
import { API_BASE_URL } from './URL_Header';

export const fetchDesigns = async () => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Design/GetAllDesigns`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) throw new Error("Failed to fetch designs");
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchDesignById = async (id) => {
  if (id == null || id === "" || Number(id) <= 0) return null;
  try {
    const response = await fetch(
      `${API_BASE_URL}/Design/GetDesign/${id}`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) throw new Error("Failed to fetch design");
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchPrintSourceUrl = async (code) => {
  const response = await fetch(
    `${API_BASE_URL}/Design/PrintSourceUrl?code=${encodeURIComponent(code || "")}`
  );
  if (!response.ok) return "";
  const data = await response.json();
  return data?.url || data?.Url || "";
};

export const replaceDesignPrintSource = async (id, file) => {
  const form = new FormData();
  form.append("file", file);
  const response = await fetch(`${API_BASE_URL}/Design/ReplacePrintSource/${id}`, {
    method: "POST",
    headers: {
      Authorization: getToken() ? `Bearer ${getToken()}` : undefined,
    },
    body: form,
  });
  if (!response.ok) {
    let message = "فشل استبدال الصورة الأصلية";
    try {
      const data = await response.json();
      message = data.message || data.Message || message;
    } catch {
      // keep the fallback
    }
    throw new Error(message);
  }
  return response.json();
};

const sharedNameDefaults = (design) =>
  Boolean(
    (design.allowsPersonalization || design.optionalPersonalization)
    && design.personalizationUsesDefaults !== false
  );

const nameSurchargePayload = (design) => {
  if (!design.optionalPersonalization || design.allowsPersonalization) return null;
  if (design.nameSurcharge === "" || design.nameSurcharge == null) return null;
  const amount = Number(design.nameSurcharge);
  return Number.isFinite(amount) ? amount : null;
};

export const fetchDesignByCode = async (code) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Design/GetDesignByCode/${code}`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) throw new Error("Failed to fetch design by code");
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchDesignNameById = async (id) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Design/GetDesignName/${id}`
    );
    if (!response.ok) throw new Error("Failed to fetch design name");
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const createDesign = async (design) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Design/AddDesign`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          categoryId: design.categoryId,
          name: design.name,
          priceBefore: design.oldPrice,
          priceAfter: design.newPrice,
          inStock: design.inStock,
          hasOffer: design.hasOffer,
          imageBase64_1: design.imageBase64_1,
          imageBase64_2: design.imageBase64_2,
          driveLink: design.driveLink,
          code: design.code || "",
          material: design.material || "",
          description: design.description || "",
          allowsPersonalization: !!design.allowsPersonalization,
          optionalPersonalization: !!design.optionalPersonalization && !design.allowsPersonalization,
          includeBackgroundInPrint: design.includeBackgroundInPrint !== false,
          nameSurcharge: nameSurchargePayload(design),
          personalizationUsesDefaults: sharedNameDefaults(design),
          requiresArabicName: !!design.requiresArabicName,
          requiresEnglishName: !!design.requiresEnglishName,
          maxNameLength: design.maxNameLength || 12,
          minNameLength: design.minNameLength || 0,
          personalizationFont: design.personalizationFont || "cairo-bold",
          personalizationFontWeight: design.personalizationFontWeight || "bold",
          personalizationFontSize: Number(design.personalizationFontSize) || 8,
          personalizationTextColor: design.personalizationTextColor || "#FFFFFF",
          personalizationPosX: Number(design.personalizationPosX) || 0,
          personalizationPosY: Number(design.personalizationPosY) || 0,
          personalizationAlignH: design.personalizationAlignH || "center",
          personalizationAlignV: design.personalizationAlignV || "middle",
          personalizationRotation: Number(design.personalizationRotation) || 0,
          personalizationTextDirection: design.personalizationTextDirection || "auto",
          personalizationConfig: sharedNameDefaults(design)
            ? { version: 2, usingSharedDefaults: true, layers: [] }
            : (design.personalizationConfig || { version: 2, layers: design.personalizationLayers || [] }),
          personalizationLayers: sharedNameDefaults(design)
            ? []
            : (design.personalizationLayers || []),
        }),
      }
    );
    if (!response.ok) throw new Error("Failed to create design");
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const updateDesign = async (id, design) => {
  try {
    const payload = {
      designId: id,
      categoryId: design.categoryId,
      name: design.name,
      priceBefore: design.oldPrice,
      priceAfter: design.newPrice,
      inStock: design.inStock,
      hasOffer: design.hasOffer,
      driveLink: design.driveLink,
      code: design.code || "",
      material: design.material || "",
      description: design.description || "",
      allowsPersonalization: !!design.allowsPersonalization,
      optionalPersonalization: !!design.optionalPersonalization && !design.allowsPersonalization,
      includeBackgroundInPrint: design.includeBackgroundInPrint !== false,
      nameSurcharge: nameSurchargePayload(design),
      personalizationUsesDefaults: sharedNameDefaults(design),
      requiresArabicName: !!design.requiresArabicName,
      requiresEnglishName: !!design.requiresEnglishName,
      maxNameLength: design.maxNameLength || 12,
      minNameLength: design.minNameLength || 0,
      personalizationFont: design.personalizationFont || "cairo-bold",
      personalizationFontWeight: design.personalizationFontWeight || "bold",
      personalizationFontSize: Number(design.personalizationFontSize) || 8,
      personalizationTextColor: design.personalizationTextColor || "#FFFFFF",
      personalizationPosX: Number(design.personalizationPosX) || 0,
      personalizationPosY: Number(design.personalizationPosY) || 0,
      personalizationAlignH: design.personalizationAlignH || "center",
      personalizationAlignV: design.personalizationAlignV || "middle",
      personalizationRotation: Number(design.personalizationRotation) || 0,
      personalizationTextDirection: design.personalizationTextDirection || "auto",
      personalizationConfig: sharedNameDefaults(design)
        ? { version: 2, usingSharedDefaults: true, layers: [] }
        : (design.personalizationConfig || { version: 2, layers: design.personalizationLayers || [] }),
      personalizationLayers: sharedNameDefaults(design)
        ? []
        : (design.personalizationLayers || []),
    };
    if (design.imageBase64_1) payload.imageBase64_1 = design.imageBase64_1;
    if (design.imageBase64_2) payload.imageBase64_2 = design.imageBase64_2;

    const response = await fetch(`${API_BASE_URL}/Design/EditDesign/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken() ? `Bearer ${getToken()}` : undefined,
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Failed to update design: ${errorData}`);
    }
    return response.status === 204 ? {} : await response.json();
  } catch (err) {
    console.error("Error updating design:", err);
    throw new Error(err.message);
  }
};

export const toggleDesignStock = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Design/ToggleInStock/${id}`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });
    if (!response.ok) throw new Error("Failed to toggle design stock");
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const deleteDesign = async (id) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Design/DeleteDesign/${id}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      let message = "فشل في حذف التصميم";
      try {
        const data = await response.json();
        message = data.message || data.Message || message;
      } catch {
        // keep default message
      }
      throw new Error(message);
    }
    return response.status === 204 ? {} : await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

const authHeaders = () => ({
  Authorization: getToken() ? `Bearer ${getToken()}` : undefined,
});

const mapFont = (item) => ({
  id: item.id || item.Id,
  name: item.name || item.Name,
  cssFamily: item.cssFamily || item.CssFamily,
  webFamily: item.webFamily || item.WebFamily,
  fileName: item.fileName || item.FileName,
  fileUrl: item.fileUrl || item.FileUrl,
  format: item.format || item.Format || "truetype",
  supportsArabic: item.supportsArabic !== false && item.SupportsArabic !== false,
  supportsEnglish: item.supportsEnglish !== false && item.SupportsEnglish !== false,
  isBuiltIn: Boolean(item.isBuiltIn ?? item.IsBuiltIn),
  isActive: item.isActive !== false && item.IsActive !== false,
});

export const fetchPersonalizationFonts = async ({ all = false } = {}) => {
  const response = await fetch(
    `${API_BASE_URL}/Design/PersonalizationFonts${all ? "?all=true" : ""}`,
    { headers: all ? authHeaders() : undefined }
  );
  if (!response.ok) throw new Error("فشل تحميل الخطوط");
  const data = await response.json();
  return (Array.isArray(data) ? data : []).map(mapFont);
};

export const createPersonalizationFont = async ({ name, supportsArabic, supportsEnglish, file }) => {
  const form = new FormData();
  form.append("name", name);
  form.append("supportsArabic", String(!!supportsArabic));
  form.append("supportsEnglish", String(!!supportsEnglish));
  if (file) form.append("file", file);
  const response = await fetch(`${API_BASE_URL}/Design/PersonalizationFonts`, {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });
  if (!response.ok) throw new Error(await readFontError(response, "فشل رفع الخط"));
  return mapFont(await response.json());
};

export const updatePersonalizationFont = async (id, { name, supportsArabic, supportsEnglish, isActive, file } = {}) => {
  const form = new FormData();
  if (name != null) form.append("name", name);
  if (supportsArabic != null) form.append("supportsArabic", String(!!supportsArabic));
  if (supportsEnglish != null) form.append("supportsEnglish", String(!!supportsEnglish));
  if (isActive != null) form.append("isActive", String(!!isActive));
  if (file) form.append("file", file);
  const response = await fetch(`${API_BASE_URL}/Design/PersonalizationFonts/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: authHeaders(),
    body: form,
  });
  if (!response.ok) throw new Error(await readFontError(response, "فشل تحديث الخط"));
  return mapFont(await response.json());
};

export const deletePersonalizationFont = async (id) => {
  const response = await fetch(`${API_BASE_URL}/Design/PersonalizationFonts/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error(await readFontError(response, "فشل حذف الخط"));
};

async function readFontError(response, fallback) {
  try {
    const text = await response.text();
    if (!text) return fallback;
    try {
      const data = JSON.parse(text);
      return data.message || data.Message || data.title || (typeof data === "string" ? data : text);
    } catch {
      return text.replace(/^"+|"+$/g, "") || fallback;
    }
  } catch {
    return fallback;
  }
}